import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Tooltip, Circle, useMapEvents, useMap } from 'react-leaflet';
import { Icon, LatLngExpression, Marker as LeafletMarker } from 'leaflet';
import { Box, Typography, Chip, useTheme, IconButton, Paper, Collapse } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { CPE } from '../types/cpe';
import { Zone } from '../types/zone';
import TabbedCreateModal from './TabbedCreateModal';
import L from 'leaflet';

// Fix for default marker icons in React-Leaflet
delete (Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom Marker component that updates icon without remounting
interface AnimatedMarkerProps {
  position: [number, number];
  icon: Icon;
  children?: React.ReactNode;
  isEnlarged?: boolean;
  eventHandlers?: {
    click?: () => void;
    mouseover?: () => void;
    mouseout?: () => void;
  };
}

const AnimatedMarker: React.FC<AnimatedMarkerProps> = ({ position, icon, children, isEnlarged, eventHandlers }) => {
  const markerRef = React.useRef<LeafletMarker>(null);

  // Update icon when it changes without remounting the marker
  React.useEffect(() => {
    const marker = markerRef.current;
    if (marker) {
      marker.setIcon(icon);
    }
  }, [icon]);

  // Manually open/close tooltip when isEnlarged changes to ensure it shows on navigation
  React.useEffect(() => {
    const marker = markerRef.current;
    if (marker) {
      // Small delay to ensure tooltip is rendered
      setTimeout(() => {
        if (isEnlarged) {
          marker.openTooltip();
        } else {
          marker.closeTooltip();
        }
      }, 50);
    }
  }, [isEnlarged]);

  return (
    <Marker ref={markerRef} position={position} icon={icon} eventHandlers={eventHandlers}>
      {children}
    </Marker>
  );
};

interface MapViewProps {
  cpes: CPE[];
  onCPEClick: (cpe: CPE) => void;
  mapControl?: {
    navigateTo: (lat: number, lng: number, zoom?: number) => void;
    selectCPE?: (cpeId: string) => void;
    deselectCPE?: () => void;
  };
  canAddCPE?: boolean;
  mapDarkMode?: boolean; // Independent map theme control
  selectedCPEId?: string | null; // Externally selected CPE (e.g., from coordinate click)
  onClearSelection?: () => void; // Callback to clear external selection
  zones?: Zone[]; // Zones to display on map
  visibleZones?: Set<string>; // Set of zone IDs that are visible
  onCreated?: () => void; // Callback when a new CPE or Zone is created
}

// Component to handle map click events
const MapClickHandler: React.FC<{ onMapClick?: (lat: number, lng: number) => void }> = ({ onMapClick }) => {
  useMapEvents({
    click(e) {
      if (onMapClick) {
        onMapClick(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
};

// Component to control map view programmatically
interface MapViewControllerProps {
  onMapReady: (navigateFn: (lat: number, lng: number, zoom?: number) => void) => void;
}

const MapViewController: React.FC<MapViewControllerProps> = ({ onMapReady }) => {
  const map = useMap();

  // Expose navigation function to parent
  React.useEffect(() => {
    const navigateTo = (lat: number, lng: number, zoom = 13) => {
      map.setView([lat, lng], zoom, {
        animate: true,
        duration: 1,
      });
    };
    onMapReady(navigateTo);
  }, [map, onMapReady]);

  return null;
};

// Get marker color based on internal_positioning and commercial_status
// If internal_positioning is "no_go" -> light gray
// Otherwise based on commercial_status:
// Uncontacted -> yellow, Contacted -> orange, O&M Offered -> red, Lost O&M -> black, O&M Contracted -> green
const getMarkerColor = (internalPositioning: string | null, commercialStatus: string | null): string => {
  // If no-go, always gray
  if (internalPositioning === 'no_go') {
    return '#9e9e9e'; // light gray
  }

  // Otherwise, color based on commercial status
  const commercialColorMap: Record<string, string> = {
    'Uncontacted': '#ffc107',     // yellow
    'Contacted': '#ff9800',       // orange
    'O&M Offered': '#f44336',     // red
    'O&M Contracted': '#4caf50',  // green
    'Lost O&M': '#212121',        // black
  };

  return commercialStatus ? commercialColorMap[commercialStatus] || '#1d388d' : '#1d388d';
};

// Get 2-letter abbreviation for the pin based on internal_positioning / commercial_status
const getMarkerAbbreviation = (internalPositioning: string | null, commercialStatus: string | null): string => {
  if (internalPositioning === 'no_go') return 'NG';

  const abbreviationMap: Record<string, string> = {
    'Uncontacted': 'UC',
    'Contacted': 'CT',
    'O&M Offered': 'OF',
    'O&M Contracted': 'OC',
    'Lost O&M': 'LO',
  };

  return commercialStatus ? abbreviationMap[commercialStatus] || '?' : '?';
};

// Create custom marker icon
// - Color based on internal_positioning and commercial_status
// - Badge (W) if is_waldevar_pp (Waldevar PP) — highest priority
// - 2-letter abbreviation of commercial status / internal positioning inside pin center
// - Diamond shape if exact_location is false
const createCustomIcon = (
  internalPositioning: string | null,
  commercialStatus: string | null,
  isWaldevarPP: boolean,
  exactLocation: boolean,
  isEnlarged = false
): Icon => {
  const color = getMarkerColor(internalPositioning, commercialStatus);
  const abbreviation = getMarkerAbbreviation(internalPositioning, commercialStatus);

  // Base size (20% larger than default)
  const baseWidth = 36;
  const baseHeight = 54;

  // Enlarged size (30% bigger than base)
  const scale = isEnlarged ? 1.3 : 1;
  const width = baseWidth * scale;
  const height = baseHeight * scale;

  // Diamond shape SVG for inexact locations
  const diamondPath = `
    <path d="M18 3L31 24L18 51L5 24L18 3Z" 
          fill="${color}" stroke="#fff" stroke-width="2.4"/>
    <path d="M18 10L25 24L18 42L11 24L18 10Z" fill="#fff"/>
  `;

  // Regular pin shape SVG for exact locations
  const pinPath = `
    <path d="M18 3C10.8 3 5 8.8 5 16c0 11.28 13 31.2 13 31.2S31 27.28 31 16C31 8.8 25.2 3 18 3z" 
          fill="${color}" stroke="#fff" stroke-width="2.4"/>
    <circle cx="18" cy="16" r="8.4" fill="#fff"/>
  `;

  // Create SVG with appropriate shape and optional Waldevar badge or abbreviation
  const svgIcon = `
    <svg width="${width}" height="${height}" viewBox="0 0 36 54" xmlns="http://www.w3.org/2000/svg">
      ${exactLocation ? pinPath : diamondPath}
      <!-- Waldevar badge (highest priority) for pin shape -->
      ${isWaldevarPP && exactLocation ? `
        <circle cx="18" cy="16" r="7.2" fill="none" stroke="#1d388d" stroke-width="2.4"/>
        <circle cx="18" cy="16" r="4.8" fill="#1d388d"/>
        <text x="18" y="19.6" font-family="Arial, sans-serif" font-size="8.4" font-weight="bold" fill="#fff" text-anchor="middle">W</text>
      ` : ''}
      <!-- Waldevar badge (highest priority) for diamond shape -->
      ${isWaldevarPP && !exactLocation ? `
        <circle cx="18" cy="24" r="5" fill="#1d388d"/>
        <text x="18" y="27" font-family="Arial, sans-serif" font-size="7" font-weight="bold" fill="#fff" text-anchor="middle">W</text>
      ` : ''}
      <!-- Abbreviation for pin shape (when not Waldevar PP) -->
      ${!isWaldevarPP && exactLocation ? `
        <text x="18" y="19.5" font-family="Arial, sans-serif" font-size="8" font-weight="bold" fill="${color}" text-anchor="middle">${abbreviation}</text>
      ` : ''}
      <!-- Abbreviation for diamond shape (when not Waldevar PP) -->
      ${!isWaldevarPP && !exactLocation ? `
        <text x="18" y="27.5" font-family="Arial, sans-serif" font-size="8" font-weight="bold" fill="${color}" text-anchor="middle">${abbreviation}</text>
      ` : ''}
    </svg>
  `;

  return new Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(svgIcon),
    iconSize: [width, height],
    iconAnchor: [width / 2, height - 2],
    popupAnchor: [0, -(height - 6)],
    className: isEnlarged ? 'marker-enlarged' : 'marker-normal',
  });
};

const MapView: React.FC<MapViewProps> = ({
  cpes,
  onCPEClick,
  mapControl,
  canAddCPE,
  mapDarkMode,
  selectedCPEId,
  onClearSelection,
  zones = [],
  visibleZones = new Set(),
  onCreated, // Destructure onCreated prop
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Use independent mapDarkMode if provided, otherwise fall back to theme mode
  const isDarkMode = React.useMemo(() => {
    return mapDarkMode !== undefined ? mapDarkMode : theme.palette.mode === 'dark';
  }, [mapDarkMode, theme.palette.mode]);

  // Force component update when theme changes
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  React.useEffect(() => {
    forceUpdate();
  }, [isDarkMode]);

  // Track hovered CPE for animation
  const [hoveredCPEId, setHoveredCPEId] = React.useState<string | null>(null);
  // Track internally selected CPE (from click)
  const [internalSelectedCPEId, setInternalSelectedCPEId] = React.useState<string | null>(null);
  // Add a modal state for tabbed PP/Zone creation
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<'pp' | 'zone'>('pp');
  const [modalCoords, setModalCoords] = useState<[number, number] | null>(null);
  const [defaultZone, setDefaultZone] = useState<Zone | null>(null);
  // Legend visibility state
  const [legendOpen, setLegendOpen] = useState(false);

  // Legend items configuration
  const legendItems = [
    { color: '#ffc107', label: t('legend.uncontacted', 'Uncontacted'), abbr: 'UC' },
    { color: '#ff9800', label: t('legend.contacted', 'Contacted'), abbr: 'CT' },
    { color: '#f44336', label: t('legend.offered', 'O&M Offered'), abbr: 'OF' },
    { color: '#4caf50', label: t('legend.contracted', 'O&M Contracted'), abbr: 'OK' },
    { color: '#212121', label: t('legend.lost', 'Lost O&M'), abbr: 'LO' },
    { color: '#9e9e9e', label: t('legend.noGo', 'No Go'), abbr: 'NG' },
  ];

  // Determine which CPE should be enlarged (hover takes precedence for immediate feedback)
  const enlargedCPEId = hoveredCPEId || selectedCPEId || internalSelectedCPEId;

  // Default center for Romania
  const defaultCenter: LatLngExpression = [45.9432, 24.9668];
  const defaultZoom = 7;

  // Handle map ready - expose navigation function and selectCPE
  const handleMapReady = React.useCallback((navigateFn: (lat: number, lng: number, zoom?: number) => void) => {
    if (mapControl) {
      mapControl.navigateTo = navigateFn;
      // Expose selectCPE method (without auto-clear - let hover control it)
      mapControl.selectCPE = (cpeId: string) => {
        setInternalSelectedCPEId(cpeId);
      };
      // Expose deselectCPE method
      mapControl.deselectCPE = () => {
        setInternalSelectedCPEId(null);
      };
    }
  }, [mapControl]);

  const handleMapClick = (lat: number, lng: number) => {
    // Find if the clicked point is within any zone
    const zoneForPoint = findZoneForPoint(lat, lng, zones);
    setDefaultZone(zoneForPoint);

    setModalCoords([lat, lng]);
    setModalOpen(true);
  };

  const getImageUrl = (imageUrl: string | null): string => {
    if (!imageUrl) return '';

    // If already a full URL, return it
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }

    // Get base URL
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
    const baseUrl = API_BASE_URL.replace('/api', '');

    // Remove leading slash if present
    const cleanImageUrl = imageUrl.startsWith('/') ? imageUrl.substring(1) : imageUrl;

    // Image URL from backend already includes 'uploads/', so just append to base
    return `${baseUrl}/${cleanImageUrl}`;
  };

  // Utility function to check if a point is within a zone's radius
  const isPointInZone = (lat: number, lng: number, zone: Zone): boolean => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat * Math.PI) / 180; // φ, λ in radians
    const φ2 = (zone.center_latitude * Math.PI) / 180;
    const Δφ = ((zone.center_latitude - lat) * Math.PI) / 180;
    const Δλ = ((zone.center_longitude - lng) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = R * c; // in meters
    return distance <= zone.radius_meters;
  };

  // Function to find the zone that contains a point (prioritize smaller zones if overlapping)
  const findZoneForPoint = (lat: number, lng: number, zones: Zone[]): Zone | null => {
    const containingZones = zones.filter(zone => isPointInZone(lat, lng, zone));

    if (containingZones.length === 0) return null;

    // If multiple zones contain the point, prefer the one with smallest radius
    return containingZones.reduce((smallest, current) =>
      current.radius_meters < smallest.radius_meters ? current : smallest
    );
  };

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        {/* Map view controller for programmatic navigation */}
        <MapViewController onMapReady={handleMapReady} />

        {/* Use dark gray tiles for dark mode, light tiles for light mode */}
        {isDarkMode ? (
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
          />
        ) : (
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        )}

        {canAddCPE && (
          <MapClickHandler onMapClick={handleMapClick} />
        )}

        {/* Tabbed modal for PP/Zone creation */}
        {modalOpen && (
          <TabbedCreateModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            coords={modalCoords}
            selectedTab={modalTab}
            onTabChange={setModalTab}
            onCreated={onCreated} // Pass onCreated to modal
            defaultZone={defaultZone}
          />
        )}

        {/* Render zone circles */}
        {zones
          .filter(zone => visibleZones.has(zone.id))
          .map((zone) => (
            <React.Fragment key={zone.id}>
              <Circle
                center={[zone.center_latitude, zone.center_longitude]}
                radius={zone.radius_meters}
                pathOptions={{
                  color: zone.color,
                  fillColor: zone.color,
                  fillOpacity: 0.1,
                  weight: 2,
                  opacity: 0.6,
                }}
              />
              {/* Zone name label */}
              <Marker
                position={[zone.center_latitude, zone.center_longitude]}
                icon={L.divIcon({
                  html: `<div style="
                    background-color: ${zone.color};
                    color: white;
                    padding: 2px 4px;
                    border-radius: 2px;
                    font-size: 10px;
                    font-weight: bold;
                    text-shadow: 1px 1px 1px rgba(0,0,0,0.7);
                    white-space: nowrap;
                    border: 1px solid white;
                    opacity: 0.42;
                  ">${zone.name}</div>`,
                  className: 'zone-label',
                  iconSize: [80, 16],
                  iconAnchor: [40, 8],
                })}
                interactive={false}
              />
            </React.Fragment>
          ))}

        {cpes.map((cpe) => {
          const lat = cpe.latitude;
          const lng = cpe.longitude;

          if (isNaN(lat) || isNaN(lng)) return null;

          const isWaldevarPP = !!cpe.is_waldevar_pp;
          const exactLocation = cpe.exact_location ?? true;
          const isEnlarged = enlargedCPEId === cpe.id;
          const icon = createCustomIcon(
            cpe.internal_positioning,
            cpe.commercial_status,
            isWaldevarPP,
            exactLocation,
            isEnlarged
          );

          return (
            <AnimatedMarker
              key={cpe.id}
              position={[lat, lng]}
              icon={icon}
              isEnlarged={isEnlarged}
              eventHandlers={{
                click: () => {
                  setInternalSelectedCPEId(cpe.id);
                  onCPEClick(cpe);
                },
                mouseover: () => {
                  setHoveredCPEId(cpe.id);
                  // Clear external selection when hovering on a different pin
                  if (selectedCPEId && selectedCPEId !== cpe.id && onClearSelection) {
                    onClearSelection();
                  }
                },
                mouseout: () => {
                  setHoveredCPEId(null);
                },
              }}
            >
                {/* Tooltip on hover - auto-positioned to avoid screen edges and pin overlap */}
                {/* Make tooltip permanent when marker is enlarged (selected or hovered) */}
                <Tooltip
                  direction="auto"
                  offset={[0, -70]}
                  opacity={0.98}
                  sticky={false}
                  permanent={isEnlarged}
                  className="custom-tooltip"
                >
                  <Box sx={{
                    minWidth: cpe.image_url ? 220 : 180,
                    maxWidth: 320,
                  }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="600"
                      gutterBottom
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        fontSize: '0.875rem',
                        mb: 1,
                      }}
                      title={cpe.name}
                    >
                      {cpe.name}
                    </Typography>

                    {cpe.image_url && (
                      <Box
                        component="img"
                        src={getImageUrl(cpe.image_url)}
                        alt={cpe.name}
                        sx={{
                          width: '100%',
                          maxHeight: 100,
                          objectFit: 'cover',
                          borderRadius: 1,
                          mb: 1,
                          display: 'block',
                        }}
                        loading="eager"
                        onError={(e) => {
                          console.error('Failed to load image:', getImageUrl(cpe.image_url));
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                      {cpe.plant_type.slice(0, 3).map((type) => (
                        <Chip
                          key={type}
                          label={type}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            fontWeight: 500,
                          }}
                        />
                      ))}
                      {cpe.plant_type.length > 3 && (
                        <Chip
                          label={`+${cpe.plant_type.length - 3}`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                    </Box>

                    {cpe.cpe_status && (
                      <Typography variant="caption" display="block" sx={{ mb: 0.25, fontSize: '0.75rem' }}>
                        {cpe.cpe_status}
                      </Typography>
                    )}

                    {cpe.power && (
                      <Typography variant="caption" display="block" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                        {t('cpe.fields.power')}: {cpe.power} MW
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              </AnimatedMarker>
            );
          })}
      </MapContainer>

      {/* Legend toggle button */}
      <IconButton
        onClick={() => setLegendOpen(!legendOpen)}
        sx={{
          position: 'absolute',
          top: 90,
          left: 10,
          zIndex: 1000,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          color: 'text.primary',
          border: `1px solid ${theme.palette.divider}`,
          backdropFilter: 'blur(10px)',
          boxShadow: 2,
          '&:hover': {
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(90, 90, 90, 0.98)' : 'rgba(240, 240, 240, 0.98)',
          },
        }}
        size="small"
        title={t('legend.toggle', 'Toggle Legend')}
      >
        <InfoIcon fontSize="small" />
      </IconButton>

      {/* Legend panel */}
      <Collapse in={legendOpen}>
        <Paper
          sx={{
            position: 'absolute',
            top: 130,
            left: 10,
            zIndex: 1000,
            p: 1.5,
            minWidth: 160,
            maxWidth: 220,
            backdropFilter: 'blur(10px)',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            color: 'text.primary',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 3,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="text.primary"
            >
              {t('legend.title', 'Legend')}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setLegendOpen(false)}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Color legend */}
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              mb: 0.5,
              display: 'block',
            }}
            color="text.primary"
          >
            {t('legend.commercialStatus', 'Commercial Status')}
          </Typography>
          {legendItems.map((item) => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  bgcolor: item.color,
                  flexShrink: 0,
                  border: theme => `1px solid ${theme.palette.divider}`,
                }}
              />
              <Typography
                variant="caption"
                sx={{ fontWeight: 'bold', minWidth: 16, color: item.color }}
              >
                {item.abbr}
              </Typography>
              <Typography
                variant="caption"
                color="text.primary"
              >
                {item.label}
              </Typography>
            </Box>
          ))}

          {/* Shape legend */}
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              mt: 1.5,
              mb: 0.5,
              display: 'block',
            }}
            color="text.primary"
          >
            {t('legend.shape', 'Pin Shape')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 14, height: 20, position: 'relative' }}>
              <Box sx={{
                width: 12,
                height: 18,
                bgcolor: '#1d388d',
                borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
              }} />
            </Box>
            <Typography
              variant="caption"
              color="text.primary"
            >
              {t('legend.exactLocation', 'Exact Location')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{
              width: 12,
              height: 12,
              bgcolor: '#1d388d',
              transform: 'rotate(45deg)',
            }} />
            <Typography
              variant="caption"
              color="text.primary"
            >
              {t('legend.approximateLocation', 'Approximate Location')}
            </Typography>
          </Box>

          {/* Waldevar badge */}
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              mt: 1.5,
              mb: 0.5,
              display: 'block',
            }}
            color="text.primary"
          >
            {t('legend.badge', 'Badge')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: '#1d388d',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '8px',
                fontWeight: 'bold',
              }}
            >
              W
            </Box>
            <Typography
              variant="caption"
              color="text.primary"
            >
              {t('legend.waldevarPP', 'Waldevar PP')}
            </Typography>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default MapView;

